import { normalizeLocale } from '@hqo/date-time/core';
import { selectBuildingLocale } from 'store/building/selectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { configSelector } from 'store/config/selectors';

/**
 * The locale every surface reads — it feeds `IntlProvider` (which catalogue) and, through 60-odd call
 * sites, every date and time formatter (how they render).
 *
 * Normalized here because this is the one place all three sources meet, and none of them is ours to
 * shape: the host config and the building record can both carry a POSIX tag, and `navigator.language`
 * is whatever the device says. `resolvePresentationLocale` downstream does NOT rescue those — it drops
 * the region and falls back, which is the wrong ANSWER rather than a crash, so nothing later can tell
 * it happened:
 *
 *     resolvePresentationLocale('en_GB.UTF-8')  ->  'en-US'   British admin reads American dates
 *     resolvePresentationLocale('fr_CA.UTF-8')  ->  'fr'      region gone
 *     normalizeLocale('en_GB.UTF-8')            ->  'en-GB'
 *
 * Safe for the catalogue axis too: `normalizeLocale` region-cases the tag (`en-gb` -> `en-GB`), and
 * `getAvailableLocale` lower-cases before matching its lowercase keys.
 */
export const useLocale = (): string => {
  const config = useSelector(configSelector);

  const buildingLocale = useSelector(selectBuildingLocale);

  return useMemo(() => {
    // Typed as strings, but this reads two redux selectors and a browser global, so the type is a
    // claim rather than a guarantee — anything non-string here used to be RETURNED, unexamined, to
    // every one of those call sites. Picking the first usable string makes the contract real, and
    // `normalizeLocale('')` yields the `en-US` default when nothing is.
    const candidate = [config.locale, buildingLocale, navigator.language].find(
      (value): value is string => typeof value === 'string' && value.trim() !== '',
    );

    return normalizeLocale(candidate ?? '');
  }, [buildingLocale, config.locale]);
};
